import { useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type { ApproachId } from '@/domain/schema/v1';
import {
  APPROACHES,
  currentStepOf,
  EVIDENCE_KIND_LABEL,
  type ApproachScore,
  type EvidenceKind,
} from '@/domain/workflow/approaches';
import type { ApproachBoard, ApproachBoardItem } from '@/services/counseling';
import { AiBridge } from '@/services/aiBridge';
import { useLoad } from '../../hooks/useLoad';
import { useRequiredSession } from '../../state/session';
import { ReceiveBox, SendButton } from '../../components/ExternalRoundTrip';
import { useService } from '../../hooks/useService';
import type { ClientOutlet } from './ClientShell';

const KINDS: EvidenceKind[] = ['need', 'indication', 'fit', 'applied'];

/**
 * 치료 방향 (A33): 회기마다 접근별 근거가 쌓이고, 가장 근거가 많은 접근을 추천한다.
 * 상담자가 채택하면 그 접근의 단계대로 진행한다. 새 방향으로 다시 시작하는 일은 드물어 작은 링크로만 둔다.
 */
export function TreatmentScreen() {
  const { client, reloadClient } = useOutletContext<ClientOutlet>();
  const svc = useService();
  const { data, reload } = useLoad(() => svc.approachBoard(client.id), [svc, client.id]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!data) return <p className="muted">불러오는 중…</p>;
  const act = (p: Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    void p
      .then(() => {
        reload();
        reloadClient();
      })
      .catch((e: Error) => setErr(e.message))
      .finally(() => setBusy(false));
  };
  const adopt = (id: ApproachId, reason?: string) => act(svc.adoptApproach(client.id, id, reason));
  return (
    <div className="stack">
      <Backfill clientId={client.id} projectUrl={client.externalProjectUrl} onDone={reload} />
      {data.plan ? (
        <PlanCard
          board={data}
          busy={busy}
          onStep={(n) => act(svc.setTreatmentStep(client.id, n))}
          onComplete={() => act(svc.completeTreatment(client.id))}
          documentsPath={`/clients/${client.id}/documents`}
        />
      ) : (
        <Recommendation board={data} busy={busy} onAdopt={adopt} />
      )}
      {data.plan && data.plan.status === 'active' && (
        <SecondaryBox
          board={data}
          busy={busy}
          onSet={(id, role) => act(svc.setSecondaryApproach(client.id, id, role))}
          onClear={() => act(svc.clearSecondaryApproach(client.id))}
        />
      )}
      {err && (
        <p role="alert" className="warn small">
          {err}
        </p>
      )}
      {data.plan ? (
        <details className="card">
          <summary>
            <strong>쌓인 근거 보기</strong>{' '}
            <span className="muted small">접근별 근거 {data.evidence.filter((e) => !e.excluded).length}개</span>
          </summary>
          <Scoreboard
            board={data}
            busy={busy}
            onExclude={(x, v) => act(svc.setApproachEvidenceExcluded(x.analysisId, x.id, v))}
          />
        </details>
      ) : (
        <section className="card">
          <h2>접근별 근거</h2>
          <Scoreboard
            board={data}
            busy={busy}
            onExclude={(x, v) => act(svc.setApproachEvidenceExcluded(x.analysisId, x.id, v))}
            onAdopt={adopt}
          />
        </section>
      )}
      {data.plan && <Restart busy={busy} current={data.plan.approach} onRestart={adopt} />}
    </div>
  );
}

/** 이름 뒤 조사: 받침이 있으면 a, 없으면 b (을/를) */
function josa(name: string, a: string, b: string): string {
  const ch = [...name.replace(/\([^)]*\)\s*$/, '')].reverse().find((c) => c >= '가' && c <= '힣');
  if (!ch) return b;
  return (ch.charCodeAt(0) - 0xac00) % 28 ? a : b;
}

function Recommendation({
  board,
  busy,
  onAdopt,
}: {
  board: ApproachBoard;
  busy: boolean;
  onAdopt: (id: ApproachId) => void;
}) {
  const r = board.recommendation;
  const sessions = new Set(board.evidence.map((e) => e.sessionNumber)).size;
  const rank: Record<EvidenceKind, number> = { indication: 0, need: 1, fit: 2, applied: 3 };
  const quotes = r.approach
    ? board.evidence
        .filter((e) => e.approach === r.approach!.id && !e.excluded)
        .sort((a, b) => rank[a.kind] - rank[b.kind])
        .slice(0, 2)
    : [];
  return (
    <section className="stack">
      {sessions > 0 && <p className="eyebrow">{sessions}회기 동안 쌓인 근거로 보면</p>}
      <h2 className="page-title">
        {r.approach
          ? `${r.approach.name}${josa(r.approach.name, '을', '를')} 추천해요`
          : '치료 방향은 아직 정하기 일러요'}
      </h2>
      <p className="muted">{r.note}</p>
      {quotes.length > 0 && (
        <div className="card">
          <strong>이런 대화가 근거가 됐어요</strong>
          {quotes.map((q) => (
            <p key={q.id} className="small" style={{ margin: 0 }}>
              “{q.quote}”{' '}
              <span className="muted">
                · {q.sessionNumber}회기 · {EVIDENCE_KIND_LABEL[q.kind]}
              </span>
            </p>
          ))}
        </div>
      )}
      {r.approach ? (
        <>
          <div className="card soft-card">
            <strong>진행하면 이렇게 돼요</strong>
            <ol className="small plain-ol">
              <li>
                {r.approach.steps.length}단계 안내가 생기고, 지금 단계에서 할 일을 알려줘요 (
                {r.approach.steps.map((s) => s.title).join(' → ')})
              </li>
              <li>다음 회기 준비 · 회기 마무리에 지금 단계가 보여요</li>
              <li>외부 AI 자료와 사례보고서가 이 이론을 주 이론으로 써요</li>
              <li>축어록 구간의 "치료 기법" 표시가 이 접근 기준으로 바뀌어요</li>
            </ol>
          </div>
          <button type="button" className="primary run-next" disabled={busy} onClick={() => onAdopt(r.approach!.id)}>
            이 방향으로 진행
          </button>
        </>
      ) : (
        <p className="muted small">
          회기 분석을 반영할 때마다 대화에서 찾은 근거(필요 신호·해야 하는 근거·부합·시행 흔적)가 접근별로 +1씩
          쌓입니다.
        </p>
      )}
    </section>
  );
}

function PlanCard({
  board,
  busy,
  onStep,
  onComplete,
  documentsPath,
}: {
  board: ApproachBoard;
  busy: boolean;
  onStep: (n: number) => void;
  onComplete: () => void;
  documentsPath: string;
}) {
  const c = currentStepOf(board.plan)!;
  const plan = board.plan!;
  const lastSession = Math.max(0, ...board.evidence.map((e) => e.sessionNumber));
  const applied = board.evidence.filter(
    (e) => e.approach === plan.approach && e.kind === 'applied' && !e.excluded && e.sessionNumber === lastSession,
  );
  return (
    <section className="stack">
      <p className="eyebrow">
        {plan.adoptedAtSession ? `${plan.adoptedAtSession}회기 뒤 채택` : '채택'} · {plan.adoptedAt.slice(0, 10)} ·{' '}
        {c.approach.scholar}
      </p>
      <h2 className="page-title">{c.approach.name}</h2>
      <div className="step-bar" aria-hidden>
        {c.approach.steps.map((s, i) => (
          <span key={s.title} className={c.completed || i < c.index ? 'done' : i === c.index ? 'current' : ''} />
        ))}
      </div>
      {c.completed ? (
        <div className="card done-card">
          <p>
            <strong>치료 완료로 표시했습니다</strong> ({plan.completedAt?.slice(0, 10)}). 종결 보고서는{' '}
            <Link to={documentsPath}>문서</Link>에서 만듭니다.
          </p>
        </div>
      ) : c.allStepsDone ? (
        <div className="step-hero">
          <span className="hero-sub">모든 단계를 마쳤어요</span>
          <p className="hero-title">치료가 끝났다고 보면 완료로 표시하세요</p>
          <button type="button" className="hero-btn" disabled={busy} onClick={onComplete}>
            치료 완료로 표시
          </button>
        </div>
      ) : (
        <div className="step-hero">
          <span className="hero-sub">
            지금 단계 · {c.index + 1}/{c.total}
          </span>
          <p className="hero-title">{c.step!.title}</p>
          <p className="hero-guide">{c.step!.guide}</p>
          {applied.length > 0 && (
            <p className="hero-sub">
              {lastSession}회기 AI 결과: 시행 흔적 {applied.length}개 (“{applied[0]!.quote}”)
            </p>
          )}
          <button type="button" className="hero-btn" disabled={busy} onClick={() => onStep(c.index + 1)}>
            이 단계 끝 →
          </button>
        </div>
      )}
      <ol className="list-card steps-list">
        {c.approach.steps.map((s, i) => {
          const state = c.completed || i < c.index ? 'done' : i === c.index ? 'current' : 'todo';
          return (
            <li key={s.title} className={`step-item step-${state}`}>
              <span className="step-mark" aria-hidden>
                {state === 'done' ? '✓' : i + 1}
              </span>
              <span className="step-name">{s.title}</span>
              {state === 'done' && !c.completed && (
                <button type="button" className="link small" disabled={busy} onClick={() => onStep(i)}>
                  되돌리기
                </button>
              )}
            </li>
          );
        })}
      </ol>
      {board.growing.length > 0 && !c.completed && (
        <p className="muted small">
          참고: 채택 뒤 다른 접근의 근거도 쌓이고 있어요 —{' '}
          {board.growing.map((g) => `${g.approach.name} ${g.total}개`).join(', ')}. 방향은 그대로 두고 참고만 하세요.
        </p>
      )}
      <div className="row" style={{ justifyContent: 'center' }}>
        {c.completed ? (
          <button type="button" className="link small" disabled={busy} onClick={() => onStep(c.total)}>
            완료 표시 취소
          </button>
        ) : (
          !c.allStepsDone && (
            <button type="button" className="small" disabled={busy} onClick={onComplete}>
              치료 완료로 표시
            </button>
          )
        )}
      </div>
    </section>
  );
}

function Scoreboard({
  board,
  busy,
  onExclude,
  onAdopt,
}: {
  board: ApproachBoard;
  busy: boolean;
  onExclude: (x: ApproachBoardItem, excluded: boolean) => void;
  onAdopt?: (id: ApproachId) => void;
}) {
  if (!board.evidence.length)
    return <p className="muted small">아직 쌓인 근거가 없습니다. 회기 분석을 반영하면 여기에 쌓입니다.</p>;
  const max = Math.max(1, ...board.scores.map((s) => s.total));
  const scored = new Set(board.scores.map((s) => s.approach.id));
  const onlyExcluded = APPROACHES.filter((a) => !scored.has(a.id) && board.evidence.some((e) => e.approach === a.id));
  return (
    <div className="stack approach-board">
      <p className="chips">
        {KINDS.map((k) => (
          <span key={k} className={`chip ak-chip ak-${k}`}>
            {EVIDENCE_KIND_LABEL[k]}
          </span>
        ))}
        <span className="muted small">근거 하나 = +1 · 줄을 누르면 근거를 보고 뺄 수 있습니다</span>
      </p>
      {board.scores.map((s) => (
        <ScoreRow
          key={s.approach.id}
          s={s}
          max={max}
          board={board}
          busy={busy}
          onExclude={onExclude}
          {...(onAdopt ? { onAdopt } : {})}
        />
      ))}
      {onlyExcluded.map((a) => (
        <details key={a.id} className="approach-row">
          <summary>
            <span className="muted">{a.name} · 모두 뺌</span>
          </summary>
          <EvidenceList items={board.evidence.filter((e) => e.approach === a.id)} busy={busy} onExclude={onExclude} />
        </details>
      ))}
    </div>
  );
}

function ScoreRow({
  s,
  max,
  board,
  busy,
  onExclude,
  onAdopt,
}: {
  s: ApproachScore;
  max: number;
  board: ApproachBoard;
  busy: boolean;
  onExclude: (x: ApproachBoardItem, excluded: boolean) => void;
  onAdopt?: (id: ApproachId) => void;
}) {
  const recommended = board.recommendation.approach?.id === s.approach.id;
  return (
    <details className="approach-row">
      <summary>
        <span className="approach-name">
          <strong>{s.approach.name}</strong>
          {recommended && <span className="badge badge-user">추천</span>}
        </span>
        <span className="approach-bar" aria-hidden>
          {KINDS.map((k) =>
            s.byKind[k] ? (
              <span key={k} className={`approach-seg ak-${k}`} style={{ width: `${(s.byKind[k] / max) * 100}%` }} />
            ) : null,
          )}
        </span>
        <span className="approach-total">{s.total}</span>
      </summary>
      <div className="stack approach-detail">
        <p className="muted small">
          {KINDS.map((k) => `${EVIDENCE_KIND_LABEL[k]} ${s.byKind[k]}`).join(' · ')} · {s.sessions}회기에 걸쳐 ·{' '}
          {s.approach.scholar}
        </p>
        <EvidenceList
          items={board.evidence.filter((e) => e.approach === s.approach.id)}
          busy={busy}
          onExclude={onExclude}
        />
        {onAdopt && !recommended && (
          <div className="row">
            <button type="button" disabled={busy} onClick={() => onAdopt(s.approach.id)}>
              이 접근으로 진행
            </button>
          </div>
        )}
      </div>
    </details>
  );
}

function EvidenceList({
  items,
  busy,
  onExclude,
}: {
  items: ApproachBoardItem[];
  busy: boolean;
  onExclude: (x: ApproachBoardItem, excluded: boolean) => void;
}) {
  return (
    <ul className="list evidence-list">
      {items.map((x) => (
        <li key={x.id} className={x.excluded ? 'muted' : undefined}>
          <span className={`chip ak-chip ak-${x.kind}`}>{EVIDENCE_KIND_LABEL[x.kind]}</span>{' '}
          <span className="muted small">{x.sessionNumber}회기</span>{' '}
          <span className={x.excluded ? 'struck' : undefined}>“{x.quote}”</span>
          {x.reason && <span className="muted small"> — {x.reason}</span>}{' '}
          <button type="button" className="link small" disabled={busy} onClick={() => onExclude(x, !x.excluded)}>
            {x.excluded ? '다시 넣기' : '빼기'}
          </button>
        </li>
      ))}
    </ul>
  );
}

/** 드문 경우: 다른 방향으로 새로 시작 (작게) */
function Restart({
  busy,
  current,
  onRestart,
}: {
  busy: boolean;
  current: ApproachId;
  onRestart: (id: ApproachId, reason?: string) => void;
}) {
  const [to, setTo] = useState<ApproachId | ''>('');
  const [reason, setReason] = useState('');
  return (
    <details className="quiet-details">
      <summary className="muted small">다른 방향으로 새로 시작</summary>
      <div className="stack">
        <p className="muted small">새로운 문제가 나타났을 때만 씁니다. 지금 방향은 기록으로 남습니다.</p>
        <select aria-label="새 접근" value={to} onChange={(e) => setTo(e.target.value as ApproachId)}>
          <option value="">접근 고르기</option>
          {APPROACHES.filter((a) => a.id !== current).map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
        <input
          aria-label="새로 시작하는 이유"
          placeholder="이유 (예: 새로 드러난 문제)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <div className="row">
          <button type="button" disabled={busy || !to} onClick={() => to && onRestart(to, reason)}>
            새 방향으로 시작
          </button>
        </div>
      </div>
    </details>
  );
}

/**
 * 이전 회기 근거 채우기: 근거 항목이 생기기 전에 반영한 회기들을 외부 AI에 한 번에 보내고, 답을 회기별로 나눠 넣는다.
 * 전사가 길어 4회기씩 나눈다. 다 채우면 이 칸은 사라진다.
 */
function Backfill({
  clientId,
  projectUrl,
  onDone,
}: {
  clientId: string;
  projectUrl?: string | undefined;
  onDone: () => void;
}) {
  const app = useRequiredSession();
  const svc = useService();
  const ai = useMemo(() => new AiBridge(app.repo, app.ctx, svc), [app, svc]);
  const { data, reload } = useLoad(() => ai.approachBackfillPacket(clientId), [ai, clientId]);
  const [result, setResult] = useState<string | null>(null);
  if (!data)
    return result ? (
      <p className="ok small" role="status">
        {result}
      </p>
    ) : null;
  return (
    <section className="card stack">
      <h2>이전 회기 근거 채우기</h2>
      <p>
        {data.sessionNumbers.join(', ')}회기는 아직 접근별 근거가 없습니다
        {data.remaining ? ` (이번에 ${data.sessionNumbers.length}개, 남은 ${data.remaining}개는 다음에)` : ''}. 외부
        AI에 한 번 보내고 답을 가져오면 회기별로 나눠 넣습니다.
      </p>
      {result && <p className="ok small">{result}</p>}
      <div className="row">
        <SendButton text={data.packet} projectUrl={projectUrl} label="외부로 보내기 (이전 회기 근거)" />
      </div>
      <ReceiveBox
        title="답 가져오기"
        hint="외부 AI의 답 전체를 복사해 오면 앱으로 돌아올 때 알아서 찾습니다."
        accept={(t) => /APPROACH_EVIDENCE_V1|"sessions"\s*:/.test(t)}
        onSubmit={async (raw) => {
          const r = await ai.importApproachBackfill(clientId, raw);
          setResult(
            `${r.sessions.join(', ')}회기에 근거 ${r.items}개를 넣었습니다.${r.warnings.length ? ` (${r.warnings.join(' / ')})` : ''}`,
          );
          reload();
          onDone();
        }}
      />
    </section>
  );
}

/**
 * 보조 접근 (A36): 주 접근의 틀 안에서 정한 자리에만 쓰는 접근 하나. 단계 안내는 주 접근만 따른다.
 * 고를 때는 근거가 많은 순서로 보여준다.
 */
function SecondaryBox({
  board,
  busy,
  onSet,
  onClear,
}: {
  board: ApproachBoard;
  busy: boolean;
  onSet: (id: ApproachId, role: string) => void;
  onClear: () => void;
}) {
  const plan = board.plan!;
  const cur = plan.secondary;
  const curDef = cur ? APPROACHES.find((a) => a.id === cur.approach) : undefined;
  const [open, setOpen] = useState(false);
  const scored = board.scores.map((x) => x.approach.id);
  const options = [
    ...board.scores.map((x) => x.approach).filter((a) => a.id !== plan.approach),
    ...APPROACHES.filter((a) => a.id !== plan.approach && !scored.includes(a.id)),
  ];
  const [pick, setPick] = useState<ApproachId | ''>(cur?.approach ?? (options[0]?.id as ApproachId | undefined) ?? '');
  const [role, setRole] = useState(cur?.role ?? '');
  const total = (id: string) => board.scores.find((x) => x.approach.id === id)?.total;
  if (!open && curDef)
    return (
      <div className="card secondary-card">
        <div className="row" style={{ alignItems: 'center', width: '100%' }}>
          <span className="status-pill">보조</span>
          <strong>{curDef.name}</strong>
          <span className="muted small">{curDef.scholar}</span>
        </div>
        <span className="small">
          {cur!.role ? `쓰는 자리: ${cur!.role}` : '쓰는 자리를 적어 두면 외부 AI·보고서가 그 자리에만 써요'}
        </span>
        <div className="row">
          <button type="button" className="small" disabled={busy} onClick={() => setOpen(true)}>
            바꾸기
          </button>
          <button type="button" className="link small" disabled={busy} onClick={onClear}>
            빼기
          </button>
        </div>
      </div>
    );
  if (!open)
    return (
      <button type="button" className="link small" style={{ alignSelf: 'flex-start' }} onClick={() => setOpen(true)}>
        ＋ 보조 접근 더하기
      </button>
    );
  return (
    <div className="card">
      <strong>보조 접근</strong>
      <span className="muted small">
        사례개념화와 단계 안내는 주 접근으로 가고, 보조 접근은 정한 자리에만 써요 (예: 정서가 막힐 때 정서중심 기법,
        신앙 갈등을 다룰 때 목회상담).
      </span>
      <label>
        접근
        <select value={pick} onChange={(e) => setPick(e.target.value as ApproachId)}>
          {options.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
              {total(a.id) ? ` · 근거 ${total(a.id)}` : ''}
            </option>
          ))}
        </select>
      </label>
      <label>
        쓰는 자리
        <input
          value={role}
          maxLength={300}
          onChange={(e) => setRole(e.target.value)}
          placeholder="예: 정서가 막히거나 회피할 때"
        />
      </label>
      <div className="row">
        <button
          type="button"
          className="primary"
          disabled={busy || !pick}
          onClick={() => {
            if (!pick) return;
            onSet(pick, role);
            setOpen(false);
          }}
        >
          보조 접근으로 정하기
        </button>
        <button type="button" className="link" onClick={() => setOpen(false)}>
          취소
        </button>
      </div>
    </div>
  );
}
